import {FormApi} from 'final-form';
import React, {useCallback, useState} from 'react';
import {useDispatch} from 'react-redux';
import {makeStyles} from '@material-ui/core';

import useModalById from '~/customHooks/useModalById';
import {addErrorNotificationAction} from '~/rootStore/notifications/notificationsReducer';
import AuthService from '~/modules/Auth/AuthService';
import logger from '~/utils/logger';
import config from '~/constants/config';
import {signInAction} from '~/modules/Auth/store/actions';
import {clearChatEasyStart} from '~/modules/App/store/actions';
import {profileTypes} from '~/constants/profiles';
import CurrentUserService from '~/modules/CurrentUser/CurrentUserService';
import {MODAL_VERIFICATION_CODE, MODAL_REGISTER_ANONYMOUSLY} from '~/rootStore/modals/modalsIds';
import {modalHideAction, modalShowAction} from '~/rootStore/modals/modalsActions';

import {PhoneVerificationForm} from '../PhoneVerification';
import ModalBase from '../ModalBase';
import ModalBaseContent from '../ModalBaseContent';

const log = logger.module('VerificationCodePopup');

const useStyles = makeStyles((theme) => ({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(2),
    margin: '10px 0px',
  },
  description: {
    margin: '15px 20px',
  },
  buttonContainer: {
    width: '100%',
  },
}));

const ModalVerificationCode: React.FC = () => {
  const s = useStyles();
  const dispatch = useDispatch();
  const {
    isOpen,
    props: verificationModalProps,
    close: onClose,
  } = useModalById(MODAL_VERIFICATION_CODE);
  const {signUpData, phone = ''} = verificationModalProps;

  const [loading, setLoading] = useState(false);
  const [tries, setTries] = useState(1);

  const {open: modalVerificationCodeOpen, close: modalVerificationCodeClose} =
    useModalById(MODAL_VERIFICATION_CODE);

  const {open: modalRegisterAnonOpen} = useModalById(MODAL_REGISTER_ANONYMOUSLY);

  const resendCode = useCallback(async () => {
    try {
      const newSignUpData = await AuthService.easySignUp({phone});
      modalVerificationCodeOpen({
        phone,
        signUpData: newSignUpData,
      });
    } catch (error) {
      log.info('Resend code error', {error});
      dispatch(addErrorNotificationAction(error.generalError));
    }
  }, [dispatch, modalVerificationCodeOpen, phone]);

  const handleSubmit = useCallback(
    async (value: string, form: FormApi) => {
      setTries(tries + 1);
      try {
        setLoading(true);
        await AuthService.easyCheckVerificationCode({signUpData, code: value});
        await CurrentUserService.createProfile(profileTypes.client);
        dispatch(signInAction({}, {autoRoute: false}));
        modalVerificationCodeClose();
        setLoading(false);
        setTimeout(form.reset);
      } catch (error) {
        setLoading(false);
        if (tries >= config.easyAuthRetryCount) {
          modalVerificationCodeClose();
          modalRegisterAnonOpen();
        } else {
          setTimeout(form.reset);
        }
      }
    },
    [dispatch, modalRegisterAnonOpen, modalVerificationCodeClose, signUpData, tries]
  );

  const handleClose = () => {
    dispatch(clearChatEasyStart());
    onClose();
  };

  const handleClickBack = useCallback(() => {
    dispatch(modalHideAction(MODAL_VERIFICATION_CODE));
    dispatch(modalShowAction(MODAL_REGISTER_ANONYMOUSLY, {phone, signUpData}));
  }, [dispatch, phone, signUpData]);

  return (
    <ModalBase showCloseButton open={isOpen} onClose={handleClose}>
      <div className={`test-modal ${s.wrapper}`}>
        <ModalBaseContent
          title="Code Verification"
          description={`Please enter the code we have sent to mobile ${phone}`}
        />
        <PhoneVerificationForm
          // @ts-expect-error onSubmit Type Error
          onSubmit={handleSubmit}
          onClickChangeNumber={handleClickBack}
          loading={loading}
          resendCode={resendCode}
          buttonTitle="Verify code"
          showResend
        />
      </div>
    </ModalBase>
  );
};

export default ModalVerificationCode;
